import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { FaLinkedinIn } from "react-icons/fa";
import {
  BadgeChips,
  GradientDivider,
} from "../LocationChapters/Components/atoms";
import { PriceCalculatorHeader } from "./PricingCalculatorComponents/PricingCalOrganisms/PriceCalculatorHeader";
import { HomePageStep } from "./PricingCalculatorComponents/PricingCalOrganisms/HomePageStep";
import { NormalPricingStep } from "./PricingCalculatorComponents/PricingCalOrganisms/NormalPricingStep";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import {
  PCPageBody,
  ProgressBar,
} from "./PricingCalculatorComponents/PriceCalMolecules/PriceCalMolecules";
import { useInfluencer } from "../../../Providers/Influencer";
import { PriceCalculatorFaqs } from "./PricingCalculatorHelpers/PriceCalHelpers";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../shadcnComponents/ui/accordion";
import { cn } from "../../../lib/utils";
import ReactDOMServer from "react-dom/server";
import React from "react";
import mixpanel from "mixpanel-browser";
import { Footer } from "../LocationChapters/Components/organisms";

type PriceCalculatorSteps =
  | "HomePage"
  | "NormalPricing"
  | "ExactPricing"
  | "ShowExactPrice";

export const PriceCalculator = () => {
  const influencerState = useInfluencer();

  const [price, setPrice] = useState({
    min: 5000,
    max: 15000,
  });
  const [normalPriceData, setNormalPriceData] = useState({
    variant: "manual",
    maxImpression: 0,
    minImpression: 0,
    NicheCategory: [],
    minFollower: 0,
    maxFollower: 0,
  });

  const { toast } = useToast();

  const [fade, setFade] = useState(false);
  const [loading, setLoading] = useState({
    active: false,
    progressPercent: 0,
  });

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] =
    useState<PriceCalculatorSteps>("HomePage");

  const formValidation = () => {
    if (normalPriceData?.NicheCategory?.length === 0) {
      toast({
        title: "Please select at least 1 category.",
        variant: "destructive",
      });
      return false;
    } else if (!normalPriceData?.minFollower) {
      toast({
        title: "Please enter the number of followers.",
        variant: "destructive",
      });
      return false;
    } else if (normalPriceData?.minFollower < 1) {
      toast({
        title: "Please enter a valid number of followers",
        variant: "destructive",
      });
      return false;
    } else if (!normalPriceData?.minImpression) {
      toast({
        title: "Please enter the minimum impressions your posts get.",
        variant: "destructive",
      });
      return false;
    } else if (normalPriceData?.minImpression < 1) {
      toast({
        title: "Please enter a valid number of minimum impressions",
        variant: "destructive",
      });
      return false;
    } else if (!normalPriceData?.maxImpression) {
      toast({
        title: "Please enter the maximum impressions your posts get",
        variant: "destructive",
      });
      return false;
    } else if (normalPriceData?.maxImpression < 1) {
      toast({
        title: "Please enter a valid number of maximum impressions",
        variant: "destructive",
      });
      return false;
    } else if (
      normalPriceData?.minImpression > 0 &&
      normalPriceData?.maxImpression > 0 &&
      normalPriceData?.minImpression > normalPriceData?.maxImpression
    ) {
      toast({
        title:
          "The minimum impressions cannot be more than the maximum impressions.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  // console.log("PriceCalculator -> normalPriceData", normalPriceData);

  const handleChangeStep = (step: PriceCalculatorSteps) => {
    setFade(true);
    setTimeout(() => {
      setCurrentStep(step);
      setFade(false);
    }, 300);
  };

  const handleSubmitManualData = async () => {
    // console.log("PriceCalculator -> normalPriceData", normalPriceData);
    mixpanel.track("PriceCalculator Homepage: Calculate Button Clicked");
    if (!formValidation()) return;

    try {
      const resData = await influencerState?.getInfluencerExactPrice({
        username: "guest-user",
        payload: normalPriceData,
      });
      if (resData?.success) {
        setPrice({
          min: resData?.data?.minExpectedSpendAmount,
          max: resData?.data?.maxExpectedSpendAmount,
        });
        setLoading((prev) => ({ ...prev, active: true, progressPercent: 0 }));

        const interval = setInterval(() => {
          setLoading((prev) => {
            const newPercent = prev.progressPercent + 1;
            if (newPercent >= 100) {
              clearInterval(interval);
              setTimeout(() => {
                setLoading((prev) => ({
                  ...prev,
                  active: false,
                  progressPercent: 0,
                }));
              }, 500);
            }
            return { ...prev, progressPercent: newPercent };
          });
        }, 30);

        handleChangeStep("NormalPricing");
      } else {
        toast({
          title: "Something went wrong",
        });
      }
    } catch (error) {
      toast({
        title: "Something went wrong",
      });
    }
  };

  return (
    <>
      <PCPageBody>
        {loading?.active && loading?.progressPercent > 0 && (
          <div className="w-1/3 h-full flex items-start justify-center  flex-col gap-3 text-center">
            <div className="w-full text-[#919191] text-center font-[Inter] text-[14px] not-italic font-normal leading-[22px]">
              Calculating your earnings… Hang tight, your estimate is on its
              way!
            </div>
            <ProgressBar progress={loading?.progressPercent} />
          </div>
        )}
        {!loading?.active &&
          (currentStep === "HomePage" || currentStep === "NormalPricing") && (
            <div className="w-full h-full flex items-start justify-center">
              <div className="inline-flex flex-col items-center gap-6">
                <BadgeChips
                  className="bg-[rgba(55,113,200,0.14)] text-[#3771C8] text-xs not-italic font-bold leading-[normal] uppercase"
                  icon={<FaLinkedinIn size={16} color="#3771C8" />}
                  badge="CALCULATE INSTANTLY!"
                />

                <PriceCalculatorHeader />

                <div
                  className={`flex w-full h-full justify-center items-center  transition-opacity duration-300 ${
                    fade ? "opacity-0" : "opacity-100"
                  }`}
                >
                  {currentStep === "HomePage" && (
                    <HomePageStep
                      data={normalPriceData}
                      setData={setNormalPriceData}
                      onSubmit={handleSubmitManualData}
                    />
                  )}

                  {currentStep === "NormalPricing" && (
                    <NormalPricingStep
                      price={{
                        min: price.min,
                        max: price.max,
                      }}
                      onAutoMode={() => {
                        mixpanel.track(
                          "PriceCalculator Homepage: Auto Mode Clicked"
                        );
                        navigate("/linkedin-pricing-calculator-auto-mode");
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
      </PCPageBody>
      <div className="flex min-h-screen items-center flex-col bg-[#171717] text-white/70">
        <main className="relative flex flex-col items-center overflow-hidden justify-center md:max-w-[1580px]  w-full min-h-[90vh] pb-5">
          <FAQArrayForPriceCalculator className="mt-[64px] md:mt-[140px] flex flex-col items-start gap-5" />
        </main>
        <div className="w-full max-w-screen-md">
          <GradientDivider className="h-[1px]" />
          <Footer />
        </div>
      </div>
    </>
  );
};

export const FAQArrayForPriceCalculator = ({
  className,
}: {
  className?: string;
}) => {
  // Convert Faqs array to JSON-LD format for structured data
  const priceCalculatorFaqs = PriceCalculatorFaqs();
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: priceCalculatorFaqs?.map((faq) => ({
      "@type": "Question",
      name: faq?.question?.toString(),
      acceptedAnswer: {
        "@type": "Answer",
        text: React.isValidElement(faq?.answer)
          ? ReactDOMServer.renderToStaticMarkup(faq?.answer)
          : faq?.answer?.toString(),
      },
    })),
  };

  return (
    <div className="md:w-1/2  w-full">
      <Accordion type="single" collapsible className={cn("w-full", className)}>
        {priceCalculatorFaqs.map((faq, index) => (
          <AccordionItem
            className="border px-6 py-1 rounded-lg border-solid border-[#313131] w-full "
            key={index}
            value={`item-${index + 1}`}
          >
            <AccordionTrigger className="text-[color:var(--Shade-White,#FFF)] text-start  text-sm md:text-base not-italic font-bold leading-[normal]">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="text-[#9A9696] text-[13px] not-italic font-normal leading-6">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </div>
  );
};
